import { A } from '@solidjs/router';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { useAgentsPreview } from '../lib/agents-preview';
import { defaultLine, roleCells } from '../lib/fleet-roles';
import { useLlmRoutes } from '../lib/llmRoutes';
import { orgHref } from '../lib/org';
import { prefersReducedMotion } from '../lib/reduced-motion';
import { SESSION_TOKEN } from '../lib/session';
import { Panel } from './Panel';

// The Fleet page's last section, AI roles and models (#6788, prototype
// `rolesSection`; it replaces the v1 footer link). The heading, the line under it,
// "Manage on the Agents page", the Default line and the nine role cells, each a
// link to the Agents page, where a role's model is changed (the Agents page has no
// per-role deep link, so every cell opens the page itself).
//
// Its own container: it reads the org's route pins (`GET /v1/llm-routes`) and the
// preview (`GET /v1/llm-routes/preview`), the Agents page's two reads, and folds
// them through `lib/fleet-roles.ts`, which calls the Agents card's own `pinFace`.
// A routes read that is not in reads "-" on every chip; a failed preview only drops
// the "→ model" part. Neither blanks the section.
//
// No "On N of M machines" line: it waits for #6757 to serve each machine's roles.

/** The section's id: the header menu's "AI roles and models" scrolls here. */
export const ROLES_ID = 'roles';

/**
 * Bring the section into view at block start (instant under reduced motion) and
 * focus its first role cell, the prototype's `toRoles`. Deferred one task so the
 * header menu has closed and returned focus before this moves it.
 */
export function showRoles(): void {
  setTimeout(() => {
    const section = document.getElementById(ROLES_ID);
    if (section === null) return;
    const reduced = prefersReducedMotion();
    section.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    section.querySelector<HTMLElement>('.fleet-role')?.focus({ preventScroll: true });
  }, 0);
}

export function FleetRoles(props: { token?: string | null }): JSX.Element {
  const token = props.token ?? SESSION_TOKEN;
  const routes = useLlmRoutes(token);
  const preview = useAgentsPreview(token);
  // Only a SERVED routes read may say "Automatic": loading and failed are not in.
  const served = () => (routes.isSuccess ? (routes.data ?? null) : null);
  const cells = () => roleCells(served(), preview.isSuccess ? preview.data : undefined);
  const line = () => defaultLine(served());
  const agentsHref = (): string => orgHref('/fleet/agents');
  return (
    <Panel
      id={ROLES_ID}
      class="fleet-roles"
      title={t('fleet.top.ai_roles')}
      action={<A href={agentsHref()}>{t('fleet.roles.manage')}</A>}
    >
      <p class="fleet-roles__lede">{t('fleet.roles.lede')}</p>
      <p class="fleet-role-default">
        <span>{t('fleet.roles.default_label')}</span>
        <span class="fleet-role__model">{line().chip}</span>
        <Show when={line().automatic}>
          <span class="fleet-role-default__note">{t('fleet.roles.default_automatic')}</span>
        </Show>
      </p>
      <div class="fleet-roles__grid">
        <For each={cells()}>
          {(cell) => (
            <A class="fleet-role" href={agentsHref()} title={t('fleet.roles.cell_title')}>
              <span class="fleet-role__name">{t(cell.name)}</span>
              <span class="fleet-role__model" title={cell.chip}>
                {cell.chip}
              </span>
              <span class="fleet-role__job">{t(cell.job)}</span>
            </A>
          )}
        </For>
      </div>
    </Panel>
  );
}
